import React, { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";

// Center-crop an image file to a square + downscale to a max edge and
// re-encode as JPEG. Runs entirely in-browser via canvas so we never
// upload a 12MP portrait when a 512px avatar is what the marketplace
// actually renders.
export const cropToSquare = (file, maxEdge = 512) =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            try {
                const side = Math.min(img.width, img.height);
                const sx = (img.width - side) / 2;
                const sy = (img.height - side) / 2;
                const target = Math.min(side, maxEdge);
                const canvas = document.createElement("canvas");
                canvas.width = target; canvas.height = target;
                const ctx = canvas.getContext("2d");
                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(url);
                    if (!blob) return reject(new Error("Photo processing failed."));
                    const base = (file.name || "photo").replace(/\.[^.]+$/, "");
                    resolve(new File([blob], `${base}.jpg`, { type: "image/jpeg" }));
                }, "image/jpeg", 0.9);
            } catch (e) { URL.revokeObjectURL(url); reject(e); }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not decode photo.")); };
        img.src = url;
    });

/**
 * Reusable profile-photo picker. Works for both talent and employers.
 *
 * Props:
 *   url         — current photo URL (or empty)
 *   onChange    — callback with the new URL after a successful upload
 *   endpoint    — API path to POST to (defaults to /auth/profile-photo)
 *   label       — Label above the picker (defaults to "Profile photo")
 *   helperText  — Small helper copy under the button (has a sensible default)
 *   testIdPrefix — data-testid prefix for the input + button (defaults to "photo")
 */
export const ProfilePhotoPicker = ({
    url,
    onChange,
    endpoint = "/auth/profile-photo",
    label = "Profile photo",
    helperText = "JPG, PNG, WebP, or HEIC · up to 15MB · we'll auto-crop to a square",
    testIdPrefix = "photo",
    // Used by the initials-fallback tile when no url is present. Falls
    // back gracefully if the caller doesn't supply these.
    name = "",
    email = "",
    userId = "",
}) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const pick = () => inputRef.current?.click();
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 15 * 1024 * 1024) {
            toast.error("Photo must be under 15MB before cropping. Try compressing first.");
            return;
        }
        setUploading(true);
        try {
            let toUpload = file;
            try {
                toUpload = await cropToSquare(file, 512);
            } catch (cropErr) {
                // Rare — HEIC on some Firefox versions. Backend still gates
                // on MIME + 5MB so falling back is safe.
                console.warn("[ProfilePhotoPicker] crop failed, uploading original:", cropErr);
            }
            const form = new FormData();
            form.append("file", toUpload);
            const { data } = await api.post(endpoint, form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 45_000,
            });
            onChange(data.profile_photo_url || data.picture);
            toast.success("Photo uploaded.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div>
            {label ? <Label>{label}</Label> : null}
            <div className={`${label ? "mt-2 " : ""}flex items-center gap-3`}>
                <Avatar url={url} name={name} email={email} userId={userId} size={64} testId={`${testIdPrefix}-avatar`} />
                <div className="flex-1">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                        onChange={onFile}
                        className="hidden"
                        data-testid={`${testIdPrefix}-file-input`}
                    />
                    <button
                        type="button"
                        onClick={pick}
                        disabled={uploading}
                        data-testid={`${testIdPrefix}-upload-btn`}
                        className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-60"
                    >
                        {uploading
                            ? <><Loader2 size={13} className="animate-spin" /> Uploading…</>
                            : <><Upload size={13} /> {url ? "Replace photo" : "Upload photo"}</>}
                    </button>
                    <p className="text-[11px] text-muted-foreground mt-1">{helperText}</p>
                </div>
            </div>
        </div>
    );
};

export default ProfilePhotoPicker;
